import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { State, Store } from '@ngrx/store';
import { CustomerServiceNote, Driver, OrganizationRiderDetail, Payment, PaymentAccount, Trip } from '@uzurv/clara';
import { isNullOrUndefined } from '@uzurv/core';
import { cloneDeep } from 'lodash';
import { filter, first, map } from 'rxjs/operators';
import { AddEditPaymentAccountDialogComponent } from '../../components/dialogs/add-edit-payment-account-dialog/add-edit-payment-account-dialog.component';
import * as OrganizationRiderActions from '../../store/organization-rider/organization-rider.actions';
import {
  AddNote,
  ConfirmSendUzurvRideAppInvitation,
  ExpandPayment,
  GetOrganizationRiderDetail,
  OpenAddOrganizationRiderDialog,
  OpenEditOrganizationRiderDetail,
  OpenEditUzurvRiderDetail,
  OpenNewPaymentDialog,
  OpenRefundDialog,
  PagePaymentsList,
  RemoveFavoriteDriver,
  RemoveIgnoredDriver,
  ResubmitPayment,
  SetNewItineraryStarted,
  SubmitPaymentForOutstandingCharges,
  TripsPagination,
  VoidPayment
} from '../../store/organization-rider/organization-rider.actions';
import { OrganizationRiderState } from '../../store/organization-rider/organization-rider.reducer';
import {
  getCustomerServiceNotes,
  getExpandedPayment,
  getExpandedPaymentTransactions,
  getFavoriteDrivers,
  getIgnoredDrivers,
  getLoadingTransactions,
  getOrganizationRiderAddress,
  getOrganizationRiderDetails,
  getOutstandingCharges,
  getPayments,
  getRider,
  getRiderPaymentAccounts,
  getRiderTrips,
  getSelectedTabIndex,
  getTripsPageIndex,
  getTripsPageSize,
  getUzurvRiderFullName,
  getUzurvRiderProfile,
  isLoadingRiderDetails,
  isOrganizationRiderAppEnabled,
  isSendingUzurvRideAppInvitation
} from '../../store/organization-rider/organization-rider.selectors';
import { BasePage } from '../base/base.page';
import { OpenTripDetailTab } from '../../store/trip-detail/trip-detail.actions';
import { takeUntil } from 'rxjs';

export enum RiderDetailTabs {
  BASIC_INFO = 0,
  RIDES = 1,
  INTERNAL_NOTES = 2,
  FAVORITE_DRIVERS = 3,
  IGNORED_DRIVERS = 4,
  MANAGE_PAYMENT_ACCOUNTS = 5,
  PAYMENTS = 6
}

@Component({
  selector: 'app-rider-details',
  templateUrl: './organization-rider-detail.page.html',
  styleUrls: ['./organization-rider-detail.page.scss']
})
export class OrganizationRiderDetailPage extends BasePage implements OnInit {
  @ViewChild('eligibilityToggle') eligibilityToggle: MatSlideToggle;

  organizationRiderDetail$ = this.store$.select(getRider);
  organizationRiderAddress$ = this.store$.select(getOrganizationRiderAddress);
  riderPaymentAccounts$ = this.store$.select(getRiderPaymentAccounts);
  ignoredDrivers$ = this.store$.select(getIgnoredDrivers);
  outstandingCharges$ = this.store$.select(getOutstandingCharges);
  payments$ = this.store$.select(getPayments);
  expandedPayment$ = this.store$.select(getExpandedPayment);
  expandedPaymentTransactions$ = this.store$.select(getExpandedPaymentTransactions);
  loadingTransactions$ = this.store$.select(getLoadingTransactions);
  isOrganizationRiderAppEnabled$ = this.store$.select(isOrganizationRiderAppEnabled);
  isSendingUzurvRideAppInvitation$ = this.store$.select(isSendingUzurvRideAppInvitation);
  customerServiceNotes$ = this.store$.select(getCustomerServiceNotes);
  uzurvRiderProfile$ = this.store$.select(getUzurvRiderProfile);
  organizationRiderDetails$ = this.store$.select(getOrganizationRiderDetails);
  isLoadingRiderDetails$ = this.store$.select(isLoadingRiderDetails);
  getUzurvRiderFullName$ = this.store$.select(getUzurvRiderFullName);
  getFavoriteDrivers$ = this.store$.select(getFavoriteDrivers);
  riderTrips$ = this.store$.select(getRiderTrips);
  selectedTabIndex$ = this.store$.select(getSelectedTabIndex);
  tripsPageIndex$ = this.store$.select(getTripsPageIndex);
  tripsPageSize$ = this.store$.select(getTripsPageSize);
  showPaymentTabs = false;

  riderIneligibleForOrganization = false;
  riderNotFound = false;
  organizationRiderDetail: OrganizationRiderDetail;

  constructor(
    private store$: Store<State<OrganizationRiderState>>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {
    super();
  }

  ngOnInit() {
    this.store$.dispatch(SetNewItineraryStarted({ isNewItinerary: false }));
    this.route.params.subscribe(params => {
      this.store$.dispatch(
        GetOrganizationRiderDetail({ payload: { organizationId: params['organizationId'], organizationRiderId: params['riderId'] } })
      );
    });

    this.organizationRiderDetails$.pipe(takeUntil(this.destroyed$)).subscribe(organizationRiderDetails => {
      if (!isNullOrUndefined(organizationRiderDetails)) {
        this.organizationRiderDetail = organizationRiderDetails[0];
        this.riderIneligibleForOrganization = organizationRiderDetails.some(organizationRiderDetails => !organizationRiderDetails.eligible);
        this.showPaymentTabs = organizationRiderDetails.some(
          organizationRiderDetails => organizationRiderDetails.riderPaymentAccountEnabled
        );
      }
    });

    // Run navigateToInitialTab() once when organizationRiderDetails are emitted.
    this.organizationRiderDetail$
      .pipe(
        filter(organizationRiderDetails => !isNullOrUndefined(organizationRiderDetails)),
        first()
      )
      .subscribe(() => {
        this.navigateToInitialTab();
      });

    this.actions$
      .pipe(
        ofType(OrganizationRiderActions.GetRiderProfileFail),
        map(() => {
          this.riderNotFound = true;
        })
      )
      .subscribe();
  }

  async navigateToInitialTab() {
    // Named "subTab" because "tab" breaks the navigation.
    const selectedTabIndex: string = this.route?.snapshot?.queryParams['subTab'];

    if (!isNullOrUndefined(selectedTabIndex)) {
      await this.handleSelectedTabChange(+selectedTabIndex);
      this.store$.dispatch(OrganizationRiderActions.SelectTabIndex({ selectedTabIndex: +selectedTabIndex }));
    }
  }

  handleEditUzurvRider(): void {
    this.store$.dispatch(OpenEditUzurvRiderDetail());
  }

  handleEditOrganizationRider(data): void {
    this.store$.dispatch(
      OpenEditOrganizationRiderDetail({
        payload: { organizationRiderDetail: data[0], organizationServiceLevels: data[2] }
      })
    );
  }

  handleAddOrganizationRider(): void {
    this.store$.dispatch(OpenAddOrganizationRiderDialog());
  }

  handleSubmitPaymentForOutstandingCharges(chargePaymentData: any): void {
    this.store$.dispatch(
      SubmitPaymentForOutstandingCharges({
        payload: {
          selectedPaymentAccount: chargePaymentData.selectedPaymentAccount,
          selectedCharges: chargePaymentData.selectedCharges
        }
      })
    );
  }

  handleDeleteFavoriteDriver(driver: Driver): void {
    this.store$.dispatch(RemoveFavoriteDriver({ driver }));
  }

  handleDeleteIgnoredDriver(driver: Driver): void {
    this.store$.dispatch(RemoveIgnoredDriver({ driver }));
  }

  handleAddPaymentAccount(): void {
    this.dialog.open(AddEditPaymentAccountDialogComponent, {
      maxWidth: '50em',
      data: {
        paymentAccount: new PaymentAccount(),
        isEditMode: false
      }
    });
  }

  handleOpenEditPaymentAccount(paymentAccount: PaymentAccount): void {
    this.dialog.open(AddEditPaymentAccountDialogComponent, {
      maxWidth: '50em',
      data: {
        paymentAccount: cloneDeep(paymentAccount),
        isEditMode: true
      }
    });
  }

  handlePagePaymentsList(pageEvent: PageEvent) {
    this.store$.dispatch(PagePaymentsList({ pageEvent }));
  }

  handeOpenNewPaymentDialog(): void {
    this.store$.dispatch(OpenNewPaymentDialog());
  }

  handleExpandPayment(payment: Payment): void {
    this.store$.dispatch(ExpandPayment({ payment }));
  }

  handleVoidPayment(payment: Payment): void {
    this.store$.dispatch(VoidPayment({ payment }));
  }

  handleResubmitPayment(payment: Payment): void {
    this.store$.dispatch(ResubmitPayment({ payment }));
  }

  handleOpenRefundDialog(payment: Payment): void {
    this.store$.dispatch(OpenRefundDialog({ payment }));
  }

  handleAddNote(note: CustomerServiceNote): void {
    this.store$.dispatch(AddNote({ payload: note }));
  }

  handleSendUzurvRideAppInvitation(rider: OrganizationRiderDetail) {
    this.store$.dispatch(ConfirmSendUzurvRideAppInvitation({ organizationRiderDetail: rider, fromNewItinerary: false }));
  }

  handleEligibilityToggle(organizationRiderDetail: OrganizationRiderDetail, isRiderEligible: boolean): void {
    this.store$.dispatch(OrganizationRiderActions.UpdateOrganizationRiderEligibility({ organizationRiderDetail, isRiderEligible }));
  }

  handleTripsPaginate(pageEvent: PageEvent): void {
    this.store$.dispatch(TripsPagination({ pageEvent }));
  }

  handleGoToTrip(trip: Trip): void {
    this.store$.dispatch(OpenTripDetailTab({ payload: { trip: trip } }));
  }

  async handleSelectedTabChange(selectedTabIndex: number): Promise<void> {
    this.store$.dispatch(OrganizationRiderActions.SelectTabIndex({ selectedTabIndex: selectedTabIndex }));
  }
}
